import { resolveCurrencySymbolText } from './componentUuidResolve';

export function parseRawNumericValue(value) {
  if (value === undefined || value === null || value === '') {
    return null;
  }
  const num = Number(String(value).replace(/,/g, ''));
  return Number.isFinite(num) ? num : null;
}

export function abbreviateLargeNumber(num) {
  const abs = Math.abs(num);
  if (abs >= 1_000_000) {
    const formatted = (num / 1_000_000).toFixed(1).replace(/\.0$/, '');
    return `${formatted}M`;
  }
  if (abs >= 10_000) {
    const formatted = (num / 1_000).toFixed(1).replace(/\.0$/, '');
    return `${formatted}K`;
  }
  return null;
}

export function formatNumberForDisplay(value) {
  if (value === undefined || value === null) {
    return value;
  }

  const formatNumeric = (num, decimalPlaces = null) => {
    const abbreviated = abbreviateLargeNumber(num);
    if (abbreviated !== null) {
      return abbreviated;
    }
    if (decimalPlaces !== null) {
      return num.toLocaleString('en-US', {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
      });
    }
    return num.toLocaleString('en-US');
  };

  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      return value;
    }
    return formatNumeric(value);
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return value;
    }

    const numericCandidate = trimmed.replace(/,/g, '');
    const parsed = parseRawNumericValue(numericCandidate);
    if (parsed === null) {
      return value;
    }

    const decimalIndex = numericCandidate.indexOf('.');
    if (decimalIndex === -1) {
      return formatNumeric(parsed);
    }

    const decimalPart = numericCandidate.slice(decimalIndex + 1);
    if (decimalPart.replace(/0+$/, '').length === 0) {
      return formatNumeric(parsed);
    }

    return formatNumeric(parsed, decimalPart.length);
  }

  const coerced = parseRawNumericValue(value);
  if (coerced !== null) {
    return formatNumeric(coerced);
  }

  return value;
}

export function formatCurrencyForDisplay(
  amount,
  currencyCode = 'USD',
  symbolOverride = ''
) {
  const num = parseRawNumericValue(amount);
  if (num === null) {
    return String(amount ?? '');
  }

  const currency = currencyCode || 'USD';
  const parts = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).formatToParts(0);
  const symbol =
    resolveCurrencySymbolText(symbolOverride) ||
    parts.find((part) => part.type === 'currency')?.value ||
    '$';
  const negative = num < 0 ? '-' : '';
  const abs = Math.abs(num);

  if (abs >= 1_000_000) {
    return `${negative}${symbol}${(abs / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  }
  if (abs >= 10_000) {
    return `${negative}${symbol}${(abs / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(num);
}

/** Compact currency labels for charts/stat cards (e.g. £16.5M, £25.0K). */
export function formatCompactCurrencyValue(
  amount,
  currencySymbol = '£',
  suffix = ''
) {
  const num = parseRawNumericValue(amount);
  if (num === null) {
    return '';
  }

  const normalizedSuffix = String(suffix ?? '').trim().toUpperCase();
  const symbol =
    resolveCurrencySymbolText(currencySymbol) || currencySymbol || '£';
  const negative = num < 0 ? '-' : '';
  const abs = Math.abs(num);

  if (normalizedSuffix === 'M') {
    return `${negative}${symbol}${(abs / 1_000_000).toFixed(1)}M`;
  }
  if (normalizedSuffix === 'K') {
    return `${negative}${symbol}${(abs / 1_000).toFixed(1)}K`;
  }

  return formatCurrencyForDisplay(num, 'USD', symbol);
}

export function applyUuidValuesDeep(target, newValuesObject) {
  if (!target || typeof target !== 'object') {
    return;
  }

  if (Array.isArray(target)) {
    target.forEach((entry) => applyUuidValuesDeep(entry, newValuesObject));
    return;
  }

  if (target.uuid && newValuesObject[target.uuid]) {
    const entry = newValuesObject[target.uuid];

    if (
      entry.value !== undefined &&
      Object.prototype.hasOwnProperty.call(target, 'value')
    ) {
      target.value = entry.value;
    }

    if (entry.hide !== undefined) {
      target.hide = entry.hide;
    }
  }

  for (let i = 1; i <= 4; i += 1) {
    const cellUuidKey = `Cell${i}uuid`;
    const cellDataKey = `Cell${i}Data`;
    if (target[cellUuidKey] && newValuesObject[target[cellUuidKey]]) {
      target[cellDataKey] = newValuesObject[target[cellUuidKey]].value;
    }

    const valueUuidKey = `Value${i}uuid`;
    const valueKey = `Value${i}`;
    if (target[valueUuidKey] && newValuesObject[target[valueUuidKey]]) {
      target[valueKey] = newValuesObject[target[valueUuidKey]].value;
    }
  }

  Object.values(target).forEach((child) => {
    if (child && typeof child === 'object') {
      applyUuidValuesDeep(child, newValuesObject);
    }
  });
}
